import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CpmButton, CpmCheckbox, CpmChip } from '../../../components/design-system';
import { CONFIG_TEMPLATE_COPY } from '../configTemplateCopy';

interface PickerFilterOption {
    value: string;
    label: string;
}

interface PickerFilterPopoverProps {
    label: string;
    header: string;
    options: PickerFilterOption[];
    selected: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
    loading?: boolean;
    loadingLine?: string;
    failed?: boolean;
    failTitle?: string;
    failBody?: string;
    onRetry?: () => void;
    onOpen?: () => void;
    search?: {
        value: string;
        onChange: (value: string) => void;
        placeholder: string;
        noHit?: boolean;
        noHitLabel?: string;
    };
}

function SearchIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cpm-text-tertiary)"
            strokeWidth="2"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.35-4.35" strokeLinecap="round" />
        </svg>
    );
}

export default function PickerFilterPopover({
    label,
    header,
    options,
    selected,
    onChange,
    disabled = false,
    loading = false,
    loadingLine,
    failed = false,
    failTitle,
    failBody,
    onRetry,
    onOpen,
    search,
}: PickerFilterPopoverProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const active = selected.length > 0;

    useEffect(() => {
        if (!open) return;
        const onDoc = (event: MouseEvent) => {
            const target = event.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        const update = () => {
            const trigger = triggerRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const width = search ? 292 : 262;
            const gap = 8;
            const panelHeight = panelRef.current?.offsetHeight ?? 260;
            const spaceBelow = window.innerHeight - rect.bottom - gap;
            const placeUp = spaceBelow < panelHeight && rect.top > spaceBelow;
            const top = placeUp ? rect.top - gap - panelHeight : rect.bottom + gap;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
            setPos({ top, left });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, options, search]);

    useEffect(() => {
        if (disabled && open) setOpen(false);
    }, [disabled, open]);

    const toggle = (value: string) => {
        onChange(
            selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]
        );
    };

    return (
        <div ref={rootRef} className="relative inline-flex">
            <span ref={triggerRef}>
                <CpmChip
                    variant="filter"
                    active={active}
                    disabled={disabled}
                    onClick={() => {
                        const next = !open;
                        setOpen(next);
                        if (next) onOpen?.();
                    }}
                >
                    {label}
                </CpmChip>
            </span>
            {open && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          ref={panelRef}
                          className="cpm-theme cpm-animate-pop-in bg-white"
                          style={{
                              position: 'fixed',
                              top: pos?.top ?? -9999,
                              left: pos?.left ?? -9999,
                              width: search ? 292 : 262,
                              zIndex: 90,
                              visibility: pos ? 'visible' : 'hidden',
                              padding: 13,
                              borderRadius: 'var(--cpm-radius-xl)',
                              border: '1px solid var(--cpm-border-default)',
                              boxShadow: 'var(--cpm-shadow-popover)',
                          }}
                      >
                          <div className="mb-2.5 flex items-baseline justify-between gap-3">
                              <span
                                  className="uppercase"
                                  style={{
                                      fontSize: 10.5,
                                      fontWeight: 800,
                                      letterSpacing: '0.08em',
                                      color: 'var(--cpm-text-tertiary)',
                                  }}
                              >
                                  {header}
                              </span>
                              <span
                                  style={{
                                      fontSize: 11.5,
                                      fontWeight: 700,
                                      color: 'var(--cpm-text-secondary)',
                                  }}
                              >
                                  {active
                                      ? `${selected.length} on`
                                      : CONFIG_TEMPLATE_COPY.NONE_SELECTED}
                              </span>
                          </div>
                          {loading ? (
                              <div className="flex flex-col" style={{ gap: 10 }}>
                                  <div className="flex items-center" style={{ gap: 8 }}>
                                      <span className="cpm-pulse-dot" />
                                      <span
                                          style={{
                                              fontSize: 12,
                                              fontWeight: 600,
                                              color: 'var(--cpm-text-secondary)',
                                          }}
                                      >
                                          {loadingLine}
                                      </span>
                                  </div>
                                  <span className="cpm-sk" style={{ width: '100%', height: 14 }} />
                                  <span className="cpm-sk" style={{ width: '78%', height: 14 }} />
                                  <span className="cpm-sk" style={{ width: '62%', height: 14 }} />
                              </div>
                          ) : null}
                          {failed ? (
                              <div className="flex flex-col" style={{ gap: 10 }}>
                                  <div
                                      style={{
                                          fontSize: 12.5,
                                          fontWeight: 700,
                                          color: 'var(--cpm-shortfall-text-med)',
                                      }}
                                  >
                                      {failTitle}
                                  </div>
                                  <div
                                      style={{
                                          fontSize: 12,
                                          fontWeight: 500,
                                          lineHeight: 1.5,
                                          color: 'var(--cpm-text-secondary)',
                                      }}
                                  >
                                      {failBody}
                                  </div>
                                  {onRetry ? (
                                      <CpmButton variant="outline" size="sm" onClick={onRetry}>
                                          {CONFIG_TEMPLATE_COPY.RETRY}
                                      </CpmButton>
                                  ) : null}
                              </div>
                          ) : null}
                          {!loading && !failed ? (
                              <>
                                  {search ? (
                                      <div className="relative mb-2.5">
                                          <span
                                              className="pointer-events-none absolute inset-y-0 left-0 flex items-center"
                                              style={{ paddingLeft: 10 }}
                                          >
                                              <SearchIcon />
                                          </span>
                                          <input
                                              type="search"
                                              value={search.value}
                                              onChange={(event) => search.onChange(event.target.value)}
                                              placeholder={search.placeholder}
                                              className="h-9 w-full rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[13px] font-semibold outline-none focus:border-[var(--cpm-brand-purple)]"
                                              style={{ paddingLeft: 30, paddingRight: 10 }}
                                          />
                                      </div>
                                  ) : null}
                                  <div
                                      className="flex flex-col overflow-y-auto"
                                      style={{ maxHeight: 214, gap: 2, margin: '0 -6px' }}
                                  >
                                      {options.map((opt) => (
                                          <div
                                              key={opt.value}
                                              className="rounded-[9px] px-1.5 py-[7px] hover:bg-[var(--cpm-purple-tint-3)]"
                                          >
                                              <CpmCheckbox
                                                  checked={selected.includes(opt.value)}
                                                  label={opt.label}
                                                  onChange={() => toggle(opt.value)}
                                              />
                                          </div>
                                      ))}
                                  </div>
                                  {search?.noHit ? (
                                      <div
                                          className="mt-2"
                                          style={{
                                              fontSize: 11.5,
                                              fontWeight: 600,
                                              color: 'var(--cpm-text-secondary)',
                                          }}
                                      >
                                          {search.noHitLabel}
                                      </div>
                                  ) : null}
                              </>
                          ) : null}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
}

export function PickerSearchInput({
    value,
    onChange,
    placeholder,
    disabled,
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    disabled?: boolean;
}) {
    return (
        <div className="relative" style={{ width: 300, flex: 'none' }}>
            <span
                className="pointer-events-none absolute inset-y-0 left-0 flex items-center"
                style={{ paddingLeft: 13 }}
            >
                <SearchIcon />
            </span>
            <input
                type="search"
                value={value}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                className="h-[38px] w-full rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[13.5px] font-semibold text-[var(--cpm-text-primary)] outline-none focus:border-[var(--cpm-brand-purple)] focus:shadow-[var(--cpm-ring-focus)] disabled:opacity-60"
                style={{ paddingLeft: 34, paddingRight: 13 }}
            />
        </div>
    );
}

export function PickerPulseLine({ children }: { children: ReactNode }) {
    return (
        <div className="flex items-center" style={{ gap: 10 }}>
            <span className="cpm-pulse-dot" />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                {children}
            </span>
        </div>
    );
}
